import { useEffect, useMemo, useState } from "react";
import { Trash2, Pencil, Plus, ArrowUpDown, Search, MessageCircle, RefreshCw, Undo2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { EditBookingModal } from "./EditBookingModal";
import { NewBookingModal } from "./NewBookingModal";
import { DepositControl } from "./DepositControl";

const STATUS_LABEL = { confirmed: "Confermata", pending: "In attesa", cancelled: "Cancellata" };
const SORTS = [
  { value: "checkin", label: "Arrivo" },
  { value: "checkout", label: "Partenza" },
  { value: "created_at", label: "Creazione" },
  { value: "total", label: "Totale" },
  { value: "guest_name", label: "Ospite" },
];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [props, setProps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [fProp, setFProp] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [sortBy, setSortBy] = useState("checkin");
  const [asc, setAsc] = useState(true);
  const [waMenu, setWaMenu] = useState(null);
  const WA_LANGS = [{ c: "it", f: "🇮🇹" }, { c: "en", f: "🇬🇧" }, { c: "de", f: "🇩🇪" }, { c: "pl", f: "🇵🇱" }];

  const load = () => {
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/apartments").then((r) => setProps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const view = useMemo(() => {
    let list = [...bookings];
    if (fProp) list = list.filter((b) => b.apartment_id === fProp);
    if (fStatus) list = list.filter((b) => b.status === fStatus);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((b) => (b.guest_name || "").toLowerCase().includes(s) || (b.guest_email || "").toLowerCase().includes(s) || (b.apartment_title || "").toLowerCase().includes(s));
    }
    list.sort((a, b) => {
      let x = a[sortBy], y = b[sortBy];
      if (sortBy === "total") { x = a.total || 0; y = b.total || 0; }
      if (x === undefined || x === null) x = "";
      if (y === undefined || y === null) y = "";
      if (x < y) return asc ? -1 : 1;
      if (x > y) return asc ? 1 : -1;
      return 0;
    });
    return list;
  }, [bookings, fProp, fStatus, q, sortBy, asc]);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [working, setWorking] = useState(false);

  const isRefundable = (b) => (b.payment_provider === "stripe" || b.payment_provider === "paypal") && (b.deposit > 0) && !b.refunded;

  const doCancel = async (withRefund) => {
    const b = cancelTarget;
    if (!b) return;
    setWorking(true);
    try {
      const { data } = await api.delete(`/admin/bookings/${b.id}`, { params: { refund: withRefund } });
      if (withRefund) toast.success(`Prenotazione eliminata · caparra €${data.refund_amount} rimborsata`);
      else toast.success(data.smoobu_cancelled ? "Prenotazione eliminata e cancellata su Smoobu" : "Prenotazione eliminata");
      setCancelTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const refundDeposit = async (b) => {
    if (!window.confirm(`Rimborsare la caparra di €${b.deposit} a ${b.guest_name}? L'ospite riceverà un'email di conferma.`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/refund`);
      toast.success(`Caparra €${data.refund_amount} rimborsata`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const syncSmoobu = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/smoobu-sync`);
      toast.success("Prenotazione inviata a Smoobu");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportExcel = async () => {
    try {
      const params = {};
      if (fProp) params.property_id = fProp;
      if (fStatus) params.status = fStatus;
      const res = await api.get("/admin/bookings/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `prenotazioni_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel esportato");
    } catch (e) { toast.error("Export non riuscito"); }
  };

  const buildWaMessage = (b) => {
    const prop = props.find((p) => p.id === b.apartment_id);
    const tpl = prop?.whatsapp_template?.trim()
      || "Ciao {nome}! Grazie per aver prenotato {struttura} dal {checkin} al {checkout}. Sono a disposizione per qualsiasi informazione.";
    return tpl
      .replaceAll("{nome}", b.guest_name || "")
      .replaceAll("{struttura}", b.apartment_title || "")
      .replaceAll("{checkin}", b.checkin || "")
      .replaceAll("{checkout}", b.checkout || "")
      .replaceAll("{ospiti}", String((b.adults ?? b.guests ?? 0) + (b.children ?? 0)))
      .replaceAll("{totale}", `€${b.total ?? ""}`);
  };

  const sendWhatsApp = async (b, lang) => {
    setWaMenu(null);
    const phone = (b.guest_phone || "").replace(/[^0-9]/g, "");
    if (!phone) return toast.error("Questa prenotazione non ha un numero di telefono");
    let msg = buildWaMessage(b);
    if (lang && lang !== "it") {
      try {
        const { data } = await api.post("/translate", { texts: [msg], target: lang });
        msg = data.translations?.[0] || msg;
      } catch (e) { toast.error("Traduzione non riuscita, invio in italiano"); }
    }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-4xl text-fir mb-1">Prenotazioni</h1>
          <p className="text-fir-deep/60">Filtra, ordina, crea, modifica o cancella le prenotazioni.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-excel-btn" onClick={exportExcel} className="px-4 py-2.5 rounded-xl border border-fir/30 text-fir hover:bg-fir/5 flex items-center gap-2 transition-colors">
            <FileSpreadsheet size={18} /> Esporta Excel
          </button>
          <button data-testid="new-booking-btn" onClick={() => setCreating(true)} className="gold-btn px-5 py-2.5 rounded-xl flex items-center gap-2">
            <Plus size={18} /> Nuova prenotazione
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-stone p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fir-deep/40" />
          <input data-testid="booking-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ospite, email, struttura…" className="input-alpine w-full pl-9" />
        </div>
        <select data-testid="filter-property" value={fProp} onChange={(e) => setFProp(e.target.value)} className="input-alpine">
          <option value="">Tutte le strutture</option>
          {props.map((p) => <option key={p.id} value={p.id}>{p.type === "hotel" ? "🏨 " : "🏠 "}{p.title}</option>)}
        </select>
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="input-alpine">
          <option value="">Tutti gli stati</option>
          <option value="confirmed">Confermata</option>
          <option value="pending">In attesa</option>
          <option value="cancelled">Cancellata</option>
        </select>
        <select data-testid="sort-by" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="input-alpine">
          {SORTS.map((s) => <option key={s.value} value={s.value}>Ordina per: {s.label}</option>)}
        </select>
        <button data-testid="sort-dir" onClick={() => setAsc(!asc)} className="p-2.5 rounded-lg border border-stone text-fir hover:bg-stone/50 flex items-center gap-1 text-sm">
          <ArrowUpDown size={16} /> {asc ? "Cresc." : "Decr."}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-stone overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-fir text-ivory">
            <tr>
              <th className="text-left px-4 py-3">Ospite</th><th className="text-left px-4 py-3">Struttura</th>
              <th className="text-left px-4 py-3">Arrivo → Partenza</th><th className="text-left px-4 py-3">Persone</th>
              <th className="text-left px-4 py-3">Totale</th><th className="text-left px-4 py-3">Canale</th>
              <th className="text-left px-4 py-3">Stato</th><th className="text-left px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {view.length === 0 && <tr><td colSpan="8" className="px-4 py-6 text-center text-fir-deep/50">Nessuna prenotazione.</td></tr>}
            {view.map((b) => (
              <tr key={b.id} data-testid="booking-row" className="border-t border-stone">
                <td className="px-4 py-3">
                  <div>{b.guest_name}</div>
                  <span className="text-xs text-fir-deep/50">{b.guest_email}</span>
                  {b.guest_phone ? <><br /><span className="text-xs text-fir-deep/50">{b.guest_phone}</span></> : null}
                  {isRefundable(b) && (
                    <button data-testid="refund-booking-btn" onClick={() => refundDeposit(b)}
                      className="mt-1.5 flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-2 py-1 transition-colors">
                      <Undo2 size={12} /> Rimborsa caparra €{b.deposit}
                    </button>
                  )}
                  {b.refunded && <div className="mt-1 text-xs text-emerald-600 flex items-center gap-1"><Undo2 size={12} /> Caparra rimborsata €{b.refund_amount}</div>}
                  {b.source !== "smoobu" && b.status !== "cancelled" && <DepositControl booking={b} />}
                </td>
                <td className="px-4 py-3">{b.apartment_title}</td>
                <td className="px-4 py-3">{b.checkin} → {b.checkout}</td>
                <td className="px-4 py-3">{b.adults ?? b.guests} ad.{b.children ? `, ${b.children} bimbi` : ""}</td>
                <td className="px-4 py-3 font-semibold">€{b.total}</td>
                <td className="px-4 py-3 text-xs">{b.source === "smoobu" ? (b.channel || "Smoobu") : "Diretta"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${b.status === "confirmed" ? "bg-green-100 text-green-700" : b.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{STATUS_LABEL[b.status] || b.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 items-center">
                    <div className="relative">
                      <button data-testid="whatsapp-booking-btn" onClick={() => setWaMenu(waMenu === b.id ? null : b.id)} title="Contatta su WhatsApp (scegli lingua)"
                        className="text-green-600 hover:text-green-700 disabled:opacity-30" disabled={!b.guest_phone}><MessageCircle size={15} /></button>
                      {waMenu === b.id && (
                        <div className="absolute z-30 right-0 mt-1 bg-white border border-stone rounded-xl shadow-xl p-1 flex gap-1" onMouseLeave={() => setWaMenu(null)}>
                          {WA_LANGS.map((l) => (
                            <button key={l.c} data-testid={`wa-lang-${b.id}-${l.c}`} onClick={() => sendWhatsApp(b, l.c)}
                              className="px-2 py-1.5 rounded-lg hover:bg-ivory text-sm" title={l.c.toUpperCase()}>{l.f}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button data-testid="edit-booking-btn" onClick={() => setEditing(b)} className="text-fir hover:text-gold"><Pencil size={14} /></button>
                    {b.source !== "smoobu" && b.status === "confirmed" && !b.smoobu_reservation_id && (
                      <button data-testid="smoobu-sync-btn" onClick={() => syncSmoobu(b.id)} title="Invia/Reinvia a Smoobu"
                        className="text-blue-600 hover:text-blue-800"><RefreshCw size={14} /></button>
                    )}
                    <button data-testid="delete-booking-btn" onClick={() => setCancelTarget(b)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cancelTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cancel-dialog">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="font-display text-2xl text-fir mb-2">Attenzione</h3>
            <p className="text-fir-deep/80 text-sm">
              Stai per cancellare la prenotazione di <b>{cancelTarget.guest_name}</b> ({cancelTarget.checkin} → {cancelTarget.checkout}).
              {(cancelTarget.source === "smoobu" || cancelTarget.smoobu_reservation_id) ? " Verrà cancellata anche su Smoobu." : ""}
            </p>
            {isRefundable(cancelTarget) ? (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="cancel-refund-notice">
                Caparra versata: <b>€{cancelTarget.deposit}</b> ({cancelTarget.payment_provider === "stripe" ? "carta / Stripe" : "PayPal"}).
                <br />Hai intenzione di rimborsare la caparra di <b>€{cancelTarget.deposit}</b> all'ospite?
              </div>
            ) : (
              <p className="mt-3 text-sm text-fir-deep/60" data-testid="cancel-norefund-notice">
                {cancelTarget.refunded ? "La caparra è già stata rimborsata." : "Nessuna caparra online rimborsabile per questa prenotazione."}
              </p>
            )}
            <div className="mt-5 flex flex-col gap-2">
              {isRefundable(cancelTarget) && (
                <button data-testid="cancel-with-refund-btn" disabled={working} onClick={() => doCancel(true)}
                  className="w-full py-2.5 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">
                  Cancella e rimborsa €{cancelTarget.deposit}
                </button>
              )}
              <button data-testid="cancel-without-refund-btn" disabled={working} onClick={() => doCancel(false)}
                className="w-full py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50">
                {isRefundable(cancelTarget) ? "Cancella SENZA rimborso" : "Elimina prenotazione"}
              </button>
              <button data-testid="cancel-abort-btn" disabled={working} onClick={() => setCancelTarget(null)}
                className="w-full py-2.5 rounded-xl border border-stone text-fir-deep hover:bg-ivory">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && <EditBookingModal booking={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <NewBookingModal properties={props} initial={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}
